import { Player, PlayerGameStatus } from '@/API';
import pencilGif from '@/assets/images/pencil.gif';
import { useSetSharingPlayer } from '@/hooks/HostSessionControlHooks';
import clsx from 'clsx';
import { motion } from 'motion/react';
import { StarIcon } from '../Icons/CommonIcons';

type PlayersListProps = {
  player: Player | null;
  isSelectionInProgress?: boolean;
  setIsSelectionInProgress?: (value: boolean) => void;
};

const PlayersListItem = ({
  player,
  isSelectionInProgress,
  setIsSelectionInProgress
}: PlayersListProps) => {
  const isConnected: boolean = player?.gameStatus === PlayerGameStatus.SETUP;
  const isSpotLighted: boolean = player?.gameStatus === PlayerGameStatus.SHARING;
  const isSelectable: boolean = player?.gameStatus === PlayerGameStatus.SUBMITTED;
  const isDrawing: boolean = player?.gameStatus === PlayerGameStatus.DRAWING;
  const isShared: boolean = player?.gameStatus === PlayerGameStatus.SHARED;

  const setSharingPlayer = useSetSharingPlayer();

  const playerListItemClickHandler = async (clickedPlayer: Player) => {
    if (!isSelectable || isSelectionInProgress) {
      return;
    }
    setIsSelectionInProgress?.(true);

    setSharingPlayer(clickedPlayer.id);
  };

  return (
    <>
      {player?.name && (
        <motion.li
          initial={{ opacity: 0, x: -300 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -300 }}
          transition={{ type: 'spring', bounce: 0.2 }}
          onClick={() => playerListItemClickHandler(player)}
          className={clsx(
            'flex items-center justify-center w-full h-[4.5rem] mb-10 rounded-full',
            isSelectable && 'bg-white cursor-pointer',
            isSpotLighted && 'bg-[#99d9d9] cursor-default',
            isConnected && 'bg-white cursor-default',
            isDrawing && 'bg-white cursor-default',
            isShared && 'bg-[#d8d8d8] cursor-default'
          )}
        >
          <div
            className={clsx(
              'border-2 h-64 w-full mx-8 my-4 rounded-full flex flex-row justify-center items-center',
              isSelectable && 'border-[#20145f]',
              isSpotLighted && 'border-white bg-[#99d9d9]',
              isShared && 'border-[#20145f]',
              isDrawing && 'border-[#20145f]',
              isConnected && 'border-[#20145f]',
              !isSelectable &&
                !isSpotLighted &&
                !isShared &&
                !isDrawing &&
                !isConnected &&
                'border-[#5c5c5c]'
            )}
          >
            {isSpotLighted && (
              <motion.div
                initial={{ opacity: 0, scale: 0, y: -50, x: -50, rotate: 0 }}
                animate={{ opacity: 1, scale: 1, y: 0, x: 0, rotate: 360 }}
                transition={{ delay: 0.4, duration: 0.3, type: 'spring', stiffness: 100 }}
                className="w-16 h-16"
              >
                <StarIcon />
              </motion.div>
            )}
            {isSpotLighted && (
              <motion.div
                initial={{ opacity: 0, scale: 0, y: -50, x: -50, rotate: 0 }}
                animate={{ opacity: 1, scale: 1, y: 0, x: 0, rotate: 360 }}
                transition={{ duration: 0.3, type: 'spring', stiffness: 100 }}
                className="w-24 h-24 mx-[5px]"
              >
                <StarIcon />
              </motion.div>
            )}
            <div className="flex justify-between">
              {isDrawing && (
                <motion.img
                  initial={{ opacity: 0, scale: 0, y: 50 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  transition={{ duration: 0.1 }}
                  className="w-24 h-24 mr-16"
                  src={pencilGif}
                />
              )}
              <div className="flex flex-col justify-center items-center">
                <p className="text-[#20145F] font-black text-lg">{player.name}</p>
                {isSpotLighted && (
                  <motion.p
                    className="text-[#20145F] italic text-sm"
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, type: 'spring', stiffness: 100 }}
                  >
                    Sharing
                  </motion.p>
                )}
              </div>
            </div>
            {isSpotLighted && (
              <motion.div
                initial={{ opacity: 0, scale: 0, y: -50, x: 50, rotate: 0 }}
                animate={{ opacity: 1, scale: 1, y: 0, x: 0, rotate: 360 }}
                transition={{ duration: 0.3, type: 'spring', stiffness: 100 }}
                className="w-24 h-24 mx-[5px]"
              >
                <StarIcon />
              </motion.div>
            )}
            {isSpotLighted && (
              <motion.div
                initial={{ opacity: 0, scale: 0, y: -50, x: 50, rotate: 0 }}
                animate={{ opacity: 1, scale: 1, y: 0, x: 0, rotate: 360 }}
                transition={{ delay: 0.4, duration: 0.3, type: 'spring', stiffness: 100 }}
                className="w-16 h-16"
              >
                <StarIcon />
              </motion.div>
            )}
          </div>
        </motion.li>
      )}
    </>
  );
};

export default PlayersListItem;
